<template>
  <div class="start-page">
    <div class="hero">
      <img
        src="/images/start-image.png"
        :alt="$t('start.heroAlt')"
        class="hero-image"
      />
    </div>

    <div class="hero-content">
      <h1 class="headline">{{ $t('start.headline') }}</h1>
      <p class="subtitle">{{ $t('start.subtitle') }}</p>

      <!--
        Same destination as the "Start quiz (September)" item in the side menu: the
        one questionnaire the team runs. Keep the two in step if the slug changes.
      -->
      <router-link to="/quiz/run/swiss-eating-quiz-version-2-september-2026" class="btn-cta">
        {{ $t('start.cta') }}
      </router-link>

      <p class="counter">
        <i18n-t keypath="start.counter" tag="span">
          <template #count>
            <strong>{{ formattedCount }}</strong>
          </template>
        </i18n-t>
      </p>

      <p class="privacy">{{ $t('start.privacy') }}</p>
    </div>

    <div class="start-footer">
      <p>
        {{ $t('start.firstTime') }}
        <router-link to="/about">{{ $t('start.firstTimeLink') }}</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';

export default {
  name: 'HomePage',
  data() {
    return {
      // TODO: replace with real fetch from backend (likely GET /quiz/responses/count)
      // when the endpoint exists. Caches for the duration of the page view.
      count: 8247,
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    formattedCount() {
      // EN uses its own convention (8,247). DE/FR/IT all use the Swiss
      // apostrophe grouper (8'247). Intl's fr-CH and it-CH locales don't
      // actually return apostrophes (they use narrow space / nothing),
      // so we force the grouping explicitly for non-EN locales.
      if (this.currentLocale === 'en') {
        return new Intl.NumberFormat('en').format(this.count);
      }
      return String(this.count).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    },
  },
};
</script>

<style scoped>
.start-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #fff;
}

/* --- Hero --- */

.hero {
  width: 100%;
  height: 320px;
  overflow: hidden;
  /* Soft green fallback while the image loads (or when missing in dev). */
  background: #C0DD97;
  flex: 0 0 auto;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

@media (min-width: 700px) {
  .hero { height: 400px; }
}

/* --- Content band --- */

.hero-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  text-align: center;
  flex: 1 1 auto;
}

.headline {
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 12px;
  color: #2c3e50;
}

@media (min-width: 700px) {
  .headline { font-size: 32px; }
}

.subtitle {
  font-size: 15px;
  color: #6c757d;
  line-height: 1.6;
  margin: 0 0 2rem;
}

/* CTA button (router-link styled as button) */

.btn-cta {
  display: inline-block;
  background: #42b983;
  color: #ffffff;
  border: none;
  padding: 14px 36px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  letter-spacing: 0.3px;
  width: 100%;
  max-width: 360px;
  box-sizing: border-box;
  transition: background-color 0.15s ease, transform 0.05s ease;
}

.btn-cta:hover { background: #36a273; }
.btn-cta:active { transform: scale(0.98); }
.btn-cta:focus-visible { outline: 2px solid #2E7D32; outline-offset: 3px; }

@media (min-width: 700px) {
  .btn-cta { width: auto; min-width: 240px; }
}

.counter {
  font-size: 14px;
  color: #6c757d;
  margin: 1rem 0 0;
}

.counter strong {
  font-weight: 500;
  color: #2c3e50;
}

.privacy {
  font-size: 12px;
  color: #adb5bd;
  margin: 0.75rem 0 0;
}

/* --- Footer link --- */

.start-footer {
  border-top: 1px solid #e9ecef;
  padding: 14px 16px;
  text-align: center;
  font-size: 13px;
  color: #6c757d;
  background: #f8f9fa;
  flex: 0 0 auto;
}

.start-footer p { margin: 0; }

.start-footer a {
  color: #42b983;
  text-decoration: none;
  font-weight: 500;
}

.start-footer a:hover { text-decoration: underline; }
</style>
